import { useEffect, useRef, useState, type DragEvent } from "react";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  ATTACHMENT_ACCEPT,
  addFiles,
  attachmentToObjectUrl,
  formatBytes,
  type ComposeAttachment,
} from "@/lib/attachments";

interface AttachmentsFieldProps {
  attachments: ComposeAttachment[];
  onChange: (next: ComposeAttachment[]) => void;
  /**
   * Test-id prefix so callers (Compose vs. inline reply) can keep their
   * existing data-testid namespaces. Optional — defaults match Compose's
   * historical names so swapping the inline widget for this component is
   * a no-op for the existing Compose end-to-end tests.
   */
  testIdPrefix?: string;
  /** Tweak the helper text shown next to the button. */
  helperText?: string;
  className?: string;
  /** Override the button label (e.g. "Attach PDF" on Compose). */
  buttonLabel?: string;
}

// Shared upload widget used by Compose and the inline reply panel.
// Supports both a click-to-pick file input and drag-and-drop onto the
// dashed drop-zone that wraps the button. All size validation lives in
// `lib/attachments` so the two surfaces stay in lockstep.
export function AttachmentsField({
  attachments,
  onChange,
  testIdPrefix = "",
  helperText = "Up to 25 MB total. PDF or image (PNG, JPEG, GIF, WebP).",
  className,
  buttonLabel = "Attach files",
}: AttachmentsFieldProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  // Index of the attachment whose full-size preview is open, or null when
  // closed. We key the dialog off the index (rather than rendering one
  // dialog per thumbnail) so there is a single shared modal and so the
  // currently-shown image can be cleared cleanly when the underlying
  // attachments change.
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  // Remember the thumbnail that opened the dialog so focus can return to
  // it on close. Radix's auto focus-restore targets the element that was
  // active at open time, which works for mouse clicks but is fragile —
  // some browsers don't focus a button on click, and Radix's restore
  // can race with our state updates. Pinning the trigger explicitly via
  // `onCloseAutoFocus` makes the round-trip deterministic.
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Per-attachment object URL for image thumbnails (null entries
  // correspond to non-image attachments like PDFs). We mint the URLs
  // in an effect rather than during render so cleanup is tied to the
  // effect's lifecycle: the cleanup callback revokes the previous
  // batch whenever `attachments` changes (one attachment removed,
  // another added, etc.) and on unmount, which prevents the Blob
  // backing each URL from being pinned in memory after the user
  // takes the file back out of the list.
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([]);

  useEffect(() => {
    const urls = attachments.map((a) => attachmentToObjectUrl(a));
    setPreviewUrls(urls);
    // If the attachment list changed underneath an open preview (e.g. the
    // user removed the file from another surface), close the dialog so we
    // don't try to render against a now-revoked object URL.
    setPreviewIdx(null);
    return () => {
      for (const url of urls) {
        if (url) URL.revokeObjectURL(url);
      }
    };
  }, [attachments]);

  const inputTestId = testIdPrefix
    ? `${testIdPrefix}-input`
    : "input-attachments";
  const buttonTestId = testIdPrefix
    ? `${testIdPrefix}-button`
    : "button-attach-file";
  const listTestId = testIdPrefix ? `${testIdPrefix}-list` : "list-attachments";
  const itemTestId = (idx: number) =>
    testIdPrefix
      ? `${testIdPrefix}-item-${idx}`
      : `attachment-item-${idx}`;
  const removeTestId = (idx: number) =>
    testIdPrefix
      ? `${testIdPrefix}-remove-${idx}`
      : `button-remove-attachment-${idx}`;
  const thumbnailTestId = (idx: number) =>
    testIdPrefix
      ? `${testIdPrefix}-thumbnail-${idx}`
      : `attachment-thumbnail-${idx}`;
  const dropZoneTestId = testIdPrefix
    ? `${testIdPrefix}-dropzone`
    : "attachments-dropzone";
  const previewDialogTestId = testIdPrefix
    ? `${testIdPrefix}-preview-dialog`
    : "attachment-preview-dialog";
  const previewImageTestId = testIdPrefix
    ? `${testIdPrefix}-preview-image`
    : "attachment-preview-image";

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || (files instanceof FileList && files.length === 0)) return;
    const { next, errors } = await addFiles(attachments, files);
    for (const err of errors) toast.error(err);
    if (next !== attachments) onChange(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAt = (idx: number) => {
    onChange(attachments.filter((_, i) => i !== idx));
  };

  // Use a depth counter so dragenter/dragleave on nested children doesn't
  // cause the highlight to flicker as the cursor crosses the button etc.
  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    if (e.dataTransfer?.types?.includes("Files")) setIsDragging(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragging(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div
        data-testid={dropZoneTestId}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={cn(
          "flex items-center gap-2 flex-wrap rounded-md border border-dashed border-border bg-background px-3 py-2 transition-colors",
          isDragging && "border-primary bg-primary/5",
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
          data-testid={inputTestId}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => fileInputRef.current?.click()}
          data-testid={buttonTestId}
        >
          <Paperclip className="w-4 h-4 mr-1.5" />
          {buttonLabel}
        </Button>
        <span className="text-xs text-muted-foreground">
          {isDragging ? "Drop files to attach" : helperText}
        </span>
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1" data-testid={listTestId}>
          {attachments.map((a, idx) => (
            <li
              key={`${a.filename}-${idx}`}
              className="flex items-center justify-between gap-2 text-sm border border-border rounded-md px-3 py-1.5 bg-muted/30"
              data-testid={itemTestId(idx)}
            >
              {previewUrls[idx] ? (
                <button
                  type="button"
                  onClick={(e) => {
                    lastTriggerRef.current = e.currentTarget;
                    setPreviewIdx(idx);
                  }}
                  className="shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label={`Preview ${a.filename}`}
                  data-testid={thumbnailTestId(idx)}
                >
                  <img
                    src={previewUrls[idx] ?? undefined}
                    alt=""
                    aria-hidden="true"
                    className="w-8 h-8 rounded object-cover border border-border bg-background block"
                  />
                </button>
              ) : null}
              <span className="truncate font-medium flex-1 min-w-0">
                {a.filename}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatBytes(a.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => removeAt(idx)}
                data-testid={removeTestId(idx)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Dialog
        open={previewIdx !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewIdx(null);
        }}
      >
        <DialogContent
          className="max-w-3xl"
          data-testid={previewDialogTestId}
          onCloseAutoFocus={(e) => {
            // Pin focus to the originating thumbnail button instead of
            // letting Radix decide. This keeps keyboard users on the
            // attachment row they zoomed into and works even when the
            // open click didn't move keyboard focus first.
            const trigger = lastTriggerRef.current;
            if (trigger && document.contains(trigger)) {
              e.preventDefault();
              trigger.focus();
            }
          }}
        >
          {previewIdx !== null && attachments[previewIdx] ? (
            <>
              <DialogHeader>
                <DialogTitle className="truncate">
                  {attachments[previewIdx].filename}
                </DialogTitle>
                <DialogDescription>
                  {formatBytes(attachments[previewIdx].size)}
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-center bg-muted/30 rounded-md overflow-hidden">
                <img
                  src={previewUrls[previewIdx] ?? undefined}
                  alt={attachments[previewIdx].filename}
                  className="max-h-[70vh] max-w-full object-contain"
                  data-testid={previewImageTestId}
                />
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
